import React, { useMemo, useState } from "react";
import { Scale, Search, X } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  getCategoryById,
  getCategoryLabel,
} from "../../constants/kpiCategories";
import { useAppSelector } from "../../../Store/Hooks";
import { businessHealthColor } from "../../../Utils/Helper/businessHealth";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";

type CategoryScore = {
  category: string;
  score: number;
};

type SpiderAccount = {
  account_id: string;
  account_name: string;
  overallScore?: number;
  categoryScores: CategoryScore[];
  /** When false, account has no Enterprise KPI data — picker tile is disabled. */
  hasData?: boolean;
};

interface SpiderCompareModalContentProps {
  allAccounts: SpiderAccount[];
  selectedAccountIds: string[];
  categoryOrder: string[];
  compareColors: string[];
  onToggleAccount: (accountId: string) => void;
  onClose: () => void;
  validationMessage?: string | null;
  embedded?: boolean;
}

const MAX_VISIBLE_COMPARE_ACCOUNTS = 4;

const getCompactAccountName = (name: string) =>
  String(name || "")
    .split(" ")
    .slice(0, 2)
    .join(" ");

const getInitials = (name: string) => {
  const parts = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (!parts.length) return "AC";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getSpiderCategoryTitle = (categoryId: string) =>
  getCategoryById(categoryId)?.label ?? getCategoryLabel(categoryId);

const SpiderCompareModalContent: React.FC<SpiderCompareModalContentProps> = ({
  allAccounts,
  selectedAccountIds,
  categoryOrder,
  compareColors,
  onToggleAccount,
  onClose,
  validationMessage = null,
  embedded = false,
}) => {
  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const getScoreColor = (score: number) =>
    businessHealthColor(score, bhThresholds) ?? "#dc2626";

  const selectedAccounts = useMemo(
    () =>
      selectedAccountIds
        .map(id => allAccounts.find(account => account.account_id === id))
        .filter(Boolean) as SpiderAccount[],
    [allAccounts, selectedAccountIds]
  );

  const visibleSelectedAccounts = useMemo(
    () => selectedAccounts.slice(0, MAX_VISIBLE_COMPARE_ACCOUNTS),
    [selectedAccounts]
  );

  const modalDisplayValidationMessage =
    selectedAccounts.length > MAX_VISIBLE_COMPARE_ACCOUNTS
      ? `Showing ${visibleSelectedAccounts.length} of ${selectedAccounts.length} available accounts in the compare chart. Deselect one active account to select another.`
      : null;

  const topValidationMessage = validationMessage ?? modalDisplayValidationMessage;

  /** Selected accounts first, then others; optional name search filters the list. */
  const bottomPickerAccounts = useMemo(() => {
    const selectedSet = new Set(selectedAccountIds);
    const query = accountSearchQuery.trim().toLowerCase();
    const matchesQuery = (account: SpiderAccount) =>
      !query ||
      String(account.account_name || "")
        .toLowerCase()
        .includes(query);

    // Keep selected accounts visible even if they don't match the search.
    const selected = selectedAccountIds
      .map(id => allAccounts.find(account => account.account_id === id))
      .filter((account): account is SpiderAccount => Boolean(account));
    const unselected = allAccounts.filter(
      account =>
        !selectedSet.has(account.account_id) && matchesQuery(account)
    );
    return [...selected, ...unselected];
  }, [allAccounts, selectedAccountIds, accountSearchQuery]);

  const averages = useMemo(() => {
    const byCategory: Record<string, number> = {};
    const accountsWithData = allAccounts.filter(
      account => account.hasData !== false
    );
    if (!accountsWithData.length) return byCategory;
    categoryOrder.forEach(category => {
      const total = accountsWithData.reduce((sum, account) => {
        const value =
          account.categoryScores.find(c => c.category === category)?.score ?? 0;
        return sum + Number(value);
      }, 0);
      byCategory[category] = Math.round(total / accountsWithData.length);
    });
    return byCategory;
  }, [allAccounts, categoryOrder]);

  const SVG_W = 700;
  const SVG_H = 520;
  const cx = SVG_W / 2;
  const cy = 250;
  const R = 155;
  const N = Math.max(1, categoryOrder.length);
  const startAngle = -Math.PI / 2;

  const angleAt = (i: number) => startAngle + (i * 2 * Math.PI) / N;
  const pointAt = (index: number, radius: number) => {
    const a = angleAt(index);
    return { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius };
  };

  const polygonPoints = (values: number[]) =>
    values
      .map((value, idx) => {
        const p = pointAt(idx, (Math.max(0, Math.min(100, value)) / 100) * R);
        return `${p.x},${p.y}`;
      })
      .join(" ");

  const portfolioValues = categoryOrder.map(category => averages[category] ?? 0);

  const [hoveredPoint, setHoveredPoint] = useState<{
    accountId: string;
    pointIdx: number;
  } | null>(null);

  const hoveredPointLabels = useMemo(() => {
    if (!hoveredPoint) return null;
    const account = selectedAccounts.find(
      a => a.account_id === hoveredPoint.accountId
    );
    if (!account) return null;
    const category = categoryOrder[hoveredPoint.pointIdx];
    if (!category) return null;
    const score = Number(
      account.categoryScores.find(c => c.category === category)?.score ?? 0
    );
    const accountIdx = selectedAccounts.findIndex(
      a => a.account_id === hoveredPoint.accountId
    );
    const accountColor =
      compareColors[Math.max(0, accountIdx) % compareColors.length];
    const tipCenter = pointAt(hoveredPoint.pointIdx, (score / 100) * R + 16);
    const bgW = 34;
    const bgH = 20;
    return {
      score,
      accountColor,
      tipX: tipCenter.x,
      scoreY: tipCenter.y,
      scoreFontSize: 13,
      bgX: tipCenter.x - bgW / 2,
      bgY: tipCenter.y - bgH / 2,
      bgW,
      bgH,
    };
  }, [hoveredPoint, selectedAccounts, categoryOrder, compareColors]);

  const compareLegend = (
    <div className="mx-6 mb-4 flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5">
      <div className="flex items-center gap-1 text-[11px] text-slate-500">
        <span className="inline-block h-[2px] w-6 border-t-2 border-dashed border-slate-400" />
        Portfolio avg
      </div>
      {visibleSelectedAccounts.map((account, idx) => (
        <div
          key={account.account_id}
          className="flex items-center gap-1 text-[11px] font-semibold"
          style={{ color: compareColors[idx % compareColors.length] }}
        >
          <span
            className="inline-block h-[3px] w-5 rounded-sm"
            style={{ backgroundColor: compareColors[idx % compareColors.length] }}
          />
          {getCompactAccountName(account.account_name)}
        </div>
      ))}
    </div>
  );

  const compareAccountPicker = (
    <div className="min-w-0 px-6 pb-4 pt-2">
      <div className="relative mb-3">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          strokeWidth={2}
          aria-hidden
        />
        <input
          type="search"
          value={accountSearchQuery}
          onChange={event => setAccountSearchQuery(event.target.value)}
          placeholder="Search accounts by name..."
          aria-label="Search accounts to compare"
          className="h-10 w-full rounded-xl border border-slate-200/90 bg-slate-50 py-2.5 pl-10 pr-10 text-sm text-slate-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-violet-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(139,92,246,0.15)] focus:ring-0 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        />
        {accountSearchQuery ? (
          <button
            type="button"
            onClick={() => setAccountSearchQuery("")}
            className="absolute right-2.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-200/80 hover:text-slate-700"
            aria-label="Clear account search"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        ) : null}
      </div>
      <div
        className="max-w-full overflow-x-auto overscroll-x-contain pb-1"
        role="region"
        aria-label="Accounts to compare"
      >
        <div className="inline-flex gap-2">
          {bottomPickerAccounts.length === 0 ? (
            <p className="m-0 px-1 py-2 text-xs text-slate-500">
              No accounts match your search.
            </p>
          ) : (
            bottomPickerAccounts.map(account => {
            const selectedIndex = selectedAccountIds.indexOf(account.account_id);
            const isSelected = selectedIndex >= 0;
            const hasKpiData = account.hasData !== false;
            const color =
              compareColors[
                isSelected ? selectedIndex : selectedAccountIds.length
              ] ?? "#64748b";
            return (
              <button
                key={account.account_id}
                type="button"
                disabled={!hasKpiData}
                aria-disabled={!hasKpiData}
                title={
                  hasKpiData
                    ? undefined
                    : "KPIs data is not available for this account"
                }
                onClick={() => {
                  if (!hasKpiData) return;
                  onToggleAccount(account.account_id);
                }}
                className={`flex w-[82px] shrink-0 flex-col items-center rounded-xl border px-2 py-2 text-center ${
                  hasKpiData
                    ? "bg-white"
                    : "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
                }`}
                style={
                  hasKpiData
                    ? {
                        borderColor: isSelected ? color : "#d6d9df",
                        boxShadow: isSelected
                          ? `inset 0 0 0 1px ${color}33`
                          : undefined,
                      }
                    : undefined
                }
              >
                <span
                  className="mb-1 flex h-8 w-8 items-center justify-center rounded-md text-xs font-bold text-white"
                  style={{
                    backgroundColor: !hasKpiData
                      ? "#94a3b8"
                      : isSelected
                        ? color
                        : "#64748b",
                  }}
                >
                  {getInitials(account.account_name)}
                </span>
                <span
                  className="max-w-[76px] truncate text-[11px] font-semibold"
                  style={{
                    color: !hasKpiData
                      ? "#94a3b8"
                      : isSelected
                        ? color
                        : "#475569",
                  }}
                >
                  {account.account_name.split(" ")[0]}
                </span>
                <span
                  className={`text-[11px] font-bold ${
                    hasKpiData ? "text-amber-600" : "text-slate-400"
                  }`}
                >
                  {hasKpiData
                    ? Math.round(Number(account.overallScore ?? 0))
                    : "-"}
                </span>
              </button>
            );
          })
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`flex min-h-0 flex-col overflow-hidden bg-white ${
        embedded ? "flex-1" : "max-h-[92vh]"
      }`}
    >
      {embedded ? null : (
        <header className="shrink-0 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3 px-6 py-4">
            <div className="flex-1">
              <h3 className="m-0 text-xl font-bold leading-tight text-slate-800">
                Account Comparison
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close comparison modal"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {topValidationMessage ? (
            <p className="mb-0 mt-0 flex items-start gap-1.5 border-t border-slate-100 bg-violet-50/40 px-6 py-2.5 text-[12px] leading-snug text-slate-600">
              <Scale
                className="mt-0.5 h-4 w-4 shrink-0 text-violet-600"
                strokeWidth={1.75}
                aria-hidden
              />
              <span>{topValidationMessage}</span>
            </p>
          ) : null}
        </header>
      )}

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
      {compareAccountPicker}
      {compareLegend}
      <div className="min-w-0 px-4 pt-2 pb-1">
        <div className="flex justify-center">
          <svg
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            className="w-full max-w-[760px]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <filter
                id="spider-hover-label-shadow"
                x="-30%"
                y="-30%"
                width="160%"
                height="160%"
              >
                <feDropShadow
                  dx="0"
                  dy="1"
                  stdDeviation="2"
                  floodColor="#0f172a"
                  floodOpacity="0.12"
                />
              </filter>
            </defs>
            {[25, 50, 75, 100].map(level => (
              <circle
                key={level}
                cx={cx}
                cy={cy}
                r={(level / 100) * R}
                fill="none"
                stroke={level === 100 ? "#bfd0e0" : "#d6dee8"}
                strokeWidth={1}
                strokeDasharray={level === 100 ? undefined : "4 4"}
              />
            ))}
            {categoryOrder.map((_, idx) => {
              const p = pointAt(idx, R);
              return (
                <line
                  key={`spoke-${idx}`}
                  x1={cx}
                  y1={cy}
                  x2={p.x}
                  y2={p.y}
                  stroke="#d6dee8"
                  strokeWidth={1}
                />
              );
            })}

            <polygon
              points={polygonPoints(portfolioValues)}
              fill="rgba(148,163,184,0.08)"
              stroke="#94a3b8"
              strokeWidth={1.5}
              strokeDasharray="5 3"
            />

            {visibleSelectedAccounts.map((account, idx) => {
              const color = compareColors[idx % compareColors.length];
              const points = categoryOrder.map(
                category =>
                  Number(
                    account.categoryScores.find(c => c.category === category)
                      ?.score ?? 0
                  )
              );
              return (
                <polygon
                  key={`poly-${account.account_id}`}
                  points={polygonPoints(points)}
                  fill={`${color}22`}
                  stroke={color}
                  strokeWidth={2.5}
                  pointerEvents="none"
                />
              );
            })}

            {visibleSelectedAccounts.map((account, idx) => {
              const color = compareColors[idx % compareColors.length];
              const points = categoryOrder.map(
                category =>
                  Number(
                    account.categoryScores.find(c => c.category === category)
                      ?.score ?? 0
                  )
              );
              return (
                <g key={`dots-${account.account_id}`}>
                  {points.map((score, pointIdx) => {
                    const p = pointAt(pointIdx, (score / 100) * R);
                    const isHovered =
                      hoveredPoint?.accountId === account.account_id &&
                      hoveredPoint.pointIdx === pointIdx;
                    return (
                      <g
                        key={`${account.account_id}-${pointIdx}`}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={() =>
                          setHoveredPoint({
                            accountId: account.account_id,
                            pointIdx,
                          })
                        }
                        onMouseLeave={() => setHoveredPoint(null)}
                      >
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={10}
                          fill="transparent"
                          pointerEvents="all"
                        />
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r={isHovered ? 6 : 4.5}
                          fill={color}
                          stroke="white"
                          strokeWidth={1.5}
                          pointerEvents="none"
                        />
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {[25, 50, 75].map(level => {
              const ringR = (level / 100) * R;
              return (
                <text
                  key={`scale-${level}`}
                  x={cx + ringR + 5}
                  y={cy}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fontSize="9"
                  fill="#94a3b8"
                  fontWeight="600"
                  pointerEvents="none"
                >
                  {level}
                </text>
              );
            })}

            {hoveredPointLabels ? (
              <g key="hovered-point-labels" pointerEvents="none">
                <rect
                  x={hoveredPointLabels.bgX}
                  y={hoveredPointLabels.bgY}
                  width={hoveredPointLabels.bgW}
                  height={hoveredPointLabels.bgH}
                  rx={8}
                  ry={8}
                  fill="#ffffff"
                  fillOpacity={0.97}
                  stroke={hoveredPointLabels.accountColor}
                  strokeWidth={1.5}
                  strokeOpacity={0.55}
                  filter="url(#spider-hover-label-shadow)"
                />
                <text
                  x={hoveredPointLabels.tipX}
                  y={hoveredPointLabels.scoreY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={hoveredPointLabels.scoreFontSize}
                  fontWeight="700"
                  fill={getScoreColor(hoveredPointLabels.score)}
                >
                  {Math.round(hoveredPointLabels.score)}
                </text>
              </g>
            ) : null}

            {categoryOrder.map((category, idx) => {
              const p = pointAt(idx, R + 46);
              const avg = averages[category] ?? 0;
              const a = Math.cos(angleAt(idx));
              const anchor = a > 0.3 ? "start" : a < -0.3 ? "end" : "middle";
              return (
                <g key={`cat-${category}`}>
                  <text
                    x={p.x}
                    y={p.y - 10}
                    textAnchor={anchor}
                    fontSize="11"
                    fontWeight="700"
                    fill="#334155"
                  >
                    {getSpiderCategoryTitle(category)}
                  </text>
                  <text
                    x={p.x}
                    y={p.y + 11}
                    textAnchor={anchor}
                    fontSize="9"
                    fill="#94a3b8"
                    fontWeight="600"
                  >
                    Avg {avg}
                  </text>
                </g>
              );
            })}

          </svg>
        </div>
      </div>

      </div>

      {!embedded ? (
        <footer className="flex shrink-0 justify-end border-t border-slate-100 bg-white px-6 py-3">
          <Button
            variant="outline"
            className="!rounded-lg !border-violet-300 !px-5 !py-2 !text-sm !font-semibold !text-violet-700 hover:!bg-violet-50"
            onClick={onClose}
          >
            Close
          </Button>
        </footer>
      ) : null}
    </div>
  );
};

export default SpiderCompareModalContent;
